import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { X, Minus, Plus, Trash, ShoppingBag, CheckCircle } from "@phosphor-icons/react";

export default function CartDrawer() {
  const { items, isOpen, closeCart, updateQty, removeItem, total, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  const goCart = () => { closeCart(); navigate("/cart"); };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        className={`fixed inset-0 z-[90] bg-black/40 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="cart-drawer-overlay"
      />
      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-[95] h-full w-full max-w-md bg-[#FAFAF8] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <ShoppingBag size={22} className="text-[#3F4E4F]" />
            <h2 className="font-serif text-2xl text-stone-900">Tu carrito</h2>
            {count > 0 && <span className="text-sm text-stone-500">({count})</span>}
          </div>
          <button onClick={closeCart} className="text-stone-400 hover:text-stone-900" data-testid="cart-drawer-close"><X size={24} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <ShoppingBag size={44} className="text-stone-300" />
            <p className="text-stone-500 mt-4">Tu carrito está vacío</p>
            <button onClick={() => { closeCart(); navigate("/shop"); }} className="mt-6 bg-[#3F4E4F] text-white px-6 py-3 hover:bg-[#2C3637]" data-testid="cart-drawer-shop">Ver productos</button>
          </div>
        ) : (
          <>
            {/* Added confirmation */}
            <div className="flex items-center gap-2 px-6 py-3 bg-[#166534]/10 text-[#166534] text-sm">
              <CheckCircle size={18} weight="fill" /> Producto agregado al carrito
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 no-scrollbar">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 py-4 border-b border-stone-100" data-testid={`drawer-item-${item.id}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-[#F0EFEB] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-base text-stone-900 leading-snug line-clamp-2">{item.name}</h3>
                    <div className="text-stone-500 text-sm mt-1">
                      ${item.unit_price.toFixed(2)}
                      {item.unit_price < item.price && <span className="ml-2 text-stone-400 line-through text-xs">${item.price.toFixed(2)}</span>}
                      {item.unit_price < item.price && <span className="ml-1 text-[#166534] text-xs">precio por cantidad</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-stone-300">
                        <button onClick={() => updateQty(item.id, item.quantity - 1)} className="px-2.5 py-1.5 hover:bg-stone-100" data-testid={`drawer-dec-${item.id}`}><Minus size={13} /></button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button onClick={() => updateQty(item.id, item.quantity + 1)} className="px-2.5 py-1.5 hover:bg-stone-100" data-testid={`drawer-inc-${item.id}`}><Plus size={13} /></button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-stone-400 hover:text-[#991B1B]" data-testid={`drawer-remove-${item.id}`}><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="font-medium text-stone-900 text-sm whitespace-nowrap">${(item.unit_price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-stone-200 px-6 py-5 space-y-4">
              <div className="flex justify-between text-lg">
                <span className="text-stone-600">Subtotal</span>
                <span className="font-medium text-stone-900" data-testid="drawer-subtotal">${total.toFixed(2)}</span>
              </div>
              <button onClick={goCheckout} className="w-full bg-[#3F4E4F] text-white py-4 hover:bg-[#2C3637] transition-colors" data-testid="drawer-checkout">Proceder al pago</button>
              <button onClick={goCart} className="w-full border border-stone-300 py-3 hover:bg-stone-100 transition-colors text-stone-700" data-testid="drawer-view-cart">Ver carrito completo</button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

